import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import SideDrawer from '../../../../../common/_custom/SideDrawer/SideDrawer';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import FinancialLoaderComponent from '../../../../../common/FinancialLoader/FinancialLoaderComponent';
import CategorySelector from '../../../../../common/CategorySelector/CategorySelector';
import EditableDropdown from '../../../../../common/EditableDropdown/EditableDropdown';
import SourceBadge from '../../../../../common/SourceBadge/SourceBadge';
import { useIsMobile } from '../../../../../common/_custom/Hooks/useIsMobile';
import { AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE } from '../../../../../utils/constants/commonConstants';
import { noDataFoundEmptyState } from '../../../../../assets/hostedassets';
import { useAuditorCommentsDrawer, formatYear } from './useAuditorCommentsDrawer';
import { useAuditedType, buildConsolidationCategoryOptions } from '../useAuditedType';
import { FinancialNature, natureToAuditedType } from '../auditedTypeUtils';
import {
    AUDITOR_COMMENTS_COLUMN_LABELS,
    AUDITOR_COMMENTS_DRAWER_TITLE,
    FETCHING_LATEST_DATA_LABEL,
    FETCH_FAILED_LABEL,
    NO_DATA_LABEL,
} from './constants';
import {
    AuditorDetails,
    AuditorCommentsContent,
    DirectorCommentsContent,
    Footnotes,
} from './components/AuditorCommentsCells/AuditorCommentsCells';
import AuditorCommentsYearPanel from './components/AuditorCommentsYearPanel/AuditorCommentsYearPanel';
import type { AuditorYearRow } from './auditorCommentsDrawer.types';
import styles from './AuditorCommentsDrawer.module.scss';

interface AuditorCommentsDrawerProps {
    orgId: string;
    pageAuditedType?: string | null;
    onClose: () => void;
}

const AuditorCommentsDrawer: React.FC<AuditorCommentsDrawerProps> = ({ orgId, pageAuditedType, onClose }) => {
    const isMobile = useIsMobile();

    // `setNature` is declared after the hook (its initial value needs the hook); this only runs after mount.
    const onConsolidationChange = (newNature: string) => setNature(newNature);

    const {
        auditedType,
        latestConsolidatedFY,
        latestStandaloneFY,
        auditedTypeOptionsObject,
        handleConsolidationChange,
        setAuditedTypeFromYears,
        getRequestNature,
        requestNature,
    } = useAuditedType(onConsolidationChange, undefined, pageAuditedType);

    // First fetch: the URL selection, else the page's, else `undefined` (backend auto-resolves).
    const [nature, setNature] = useState<string | undefined>(getRequestNature);

    const { loading, rows, lastUpdated, isMcaCalled, fetchFailed } = useAuditorCommentsDrawer(orgId, nature, setAuditedTypeFromYears);

    const hasNatureChoice = rows.length > 0 && (latestConsolidatedFY !== null || latestStandaloneFY !== null);
    const consolidationToggle = hasNatureChoice && (
        <div className={clsx({ [styles.NatureToggle]: !isMobile })}>
            <CategorySelector
                categoryOptions={buildConsolidationCategoryOptions(auditedTypeOptionsObject)}
                selectedCategory={auditedType}
                changeSelectedTab={(type) => handleConsolidationChange(type)}
                equalWidth={!isMobile}
            />
        </div>
    );
    const failedNatureDropdown = !loading && fetchFailed && (
        <EditableDropdown
            id="AuditorCommentsNature"
            value={natureToAuditedType(nature as FinancialNature)}
            options={[AUDITED_TYPE_STANDALONE, AUDITED_TYPE_CONSOLIDATED]}
            setOutput={(type: string) => setNature(requestNature(type))}
            removeInput
            readOnly
            wrapperClassName={styles.NatureDropdown}
            inputClassName={styles.NatureDropdownInput}
            dropdownClassName={styles.NatureDropdownMenu}
        />
    );
    const consolidationSelector = failedNatureDropdown || consolidationToggle;
    const mcaFetchingStatus = isMcaCalled && (
        <span className={styles.LoaderMessage}>
            <LoadingSpinner color="var(--primary-text-colour)" height={isMobile ? '1rem' : '1.125rem'} className={styles.LoaderSpinner} />
            {FETCHING_LATEST_DATA_LABEL}
        </span>
    );
    const lastUpdatedMeta = lastUpdated && <span className={styles.MetaItem}>Last Updated on {lastUpdated}</span>;
    const sourceBadge = !loading && <SourceBadge value="MCA" className={styles.HeaderSourceBadge} />;
    const hasMobileControls = !!(consolidationSelector || sourceBadge);

    // Portalled to body: mounted in place, the drawer inherits whatever stacking context its
    // caller sits in (e.g. a sticky header), and anything outside that context with a higher
    // z-index paints over it — its own z-index: 999 cannot escape.
    return createPortal(
        <SideDrawer
            onClose={onClose}
            width="88.33%"
            heading={AUDITOR_COMMENTS_DRAWER_TITLE}
            closeIcon={isMobile ? undefined : <CloseIcon className={styles.CloseIcon} />}
        >
            <SideDrawer.MobileLayout>
                {isMobile ? (
                    <SideDrawer.MobileHeader
                        title={AUDITOR_COMMENTS_DRAWER_TITLE}
                        status={
                            <>
                                {mcaFetchingStatus}
                                {lastUpdatedMeta}
                            </>
                        }
                        controls={
                            hasMobileControls && (
                                <>
                                    {consolidationSelector}
                                    {sourceBadge}
                                </>
                            )
                        }
                    />
                ) : (
                    <div className={styles.DrawerHeader}>
                        <div className={styles.HeaderContainer}>
                            <div className={styles.HeaderLeft}>
                                <span className={styles.HeaderTitle}>{AUDITOR_COMMENTS_DRAWER_TITLE}</span>
                                {consolidationSelector}
                            </div>
                            <div className={clsx(styles.HeaderRight, { [styles.FailedHeaderRight]: fetchFailed })}>
                                {mcaFetchingStatus}
                                {lastUpdatedMeta}
                                {sourceBadge}
                            </div>
                        </div>
                    </div>
                )}
                <div className={clsx(styles.DrawerContent, { [styles.DrawerContentBelowHeader]: isMobile && !hasMobileControls })}>
                    {loading && (
                        <FinancialLoaderComponent
                            tableName={AUDITOR_COMMENTS_DRAWER_TITLE}
                            message="Fetching auditor comments data"
                            status={loading}
                        />
                    )}
                    {!loading && rows.length > 0 && isMobile && (
                        <div className={styles.PanelList} key={auditedType}>
                            {rows.map((row, i) => (
                                <AuditorCommentsYearPanel key={row.year} row={row} defaultExpanded={i === 0} isMobile={isMobile} />
                            ))}
                        </div>
                    )}
                    {!loading && (!isMobile || rows.length === 0) && (
                        <table className={clsx(styles.Table, { [styles.FailedTable]: fetchFailed })}>
                            <thead>
                                <tr className={styles.TableHeader}>
                                    <th className={styles.YearCell}>{AUDITOR_COMMENTS_COLUMN_LABELS.YEAR}</th>
                                    <th className={styles.AuditorDetailsCell}>{AUDITOR_COMMENTS_COLUMN_LABELS.AUDITOR_DETAILS}</th>
                                    <th className={styles.AuditorCommentsColumn}>{AUDITOR_COMMENTS_COLUMN_LABELS.AUDITOR_COMMENTS}</th>
                                    <th>{AUDITOR_COMMENTS_COLUMN_LABELS.DIRECTOR_COMMENTS}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length > 0 ? (
                                    rows.map((row) => <AuditorYearTableRows key={row.year} row={row} isMobile={isMobile} />)
                                ) : fetchFailed ? (
                                    <tr className={`${styles.TableRow} ${styles.UnavailableDataTableRow}`}>
                                        <td colSpan={4}>
                                            <div className={styles.UnavailableDataRow}>
                                                <InfoIcon className={styles.UnavailableDataIcon} aria-hidden />
                                                <span className={styles.UnavailableDataMessage}>{FETCH_FAILED_LABEL}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr className={`${styles.TableRow} ${styles.EmptyStateTableRow}`}>
                                        <td colSpan={4}>
                                            <div className={styles.EmptyState}>
                                                <img
                                                    className={styles.EmptyStateIllustration}
                                                    src={noDataFoundEmptyState}
                                                    alt=""
                                                    aria-hidden
                                                />
                                                <span className={styles.EmptyStateMessage}>{NO_DATA_LABEL}</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </SideDrawer.MobileLayout>
        </SideDrawer>,
        document.body,
    );
};

export default AuditorCommentsDrawer;

const AuditorYearTableRows = ({ row, isMobile }: { row: AuditorYearRow; isMobile: boolean }) => {
    const hasFootnotes = row.auditorFootnotes.length > 0 || row.directorFootnotes.length > 0;
    const spannedRows = hasFootnotes ? 2 : undefined;
    return (
        <>
            <tr className={clsx(styles.TableRow, { [styles.RowAboveFootnotes]: hasFootnotes })}>
                <td className={styles.YearCell} rowSpan={spannedRows}>
                    {formatYear(row.year)}
                </td>
                <td className={styles.AuditorDetailsCell} rowSpan={spannedRows}>
                    <AuditorDetails row={row} isMobile={isMobile} />
                </td>
                <td>
                    <AuditorCommentsContent row={row} showFootnotes={false} />
                </td>
                <td>
                    <DirectorCommentsContent row={row} showFootnotes={false} />
                </td>
            </tr>
            {hasFootnotes && (
                <tr className={clsx(styles.TableRow, styles.FootnoteRow)}>
                    <td>
                        <Footnotes footnotes={row.auditorFootnotes} />
                    </td>
                    <td>
                        <Footnotes footnotes={row.directorFootnotes} />
                    </td>
                </tr>
            )}
        </>
    );
};
